import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { MapPin, Search } from "lucide-react";
import SmartDatePicker from "../../../components/SmartDatePicker";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const FramerMotion = require("framer-motion");
const motion = FramerMotion.motion;
const AnimatePresence = FramerMotion.AnimatePresence;

const SEARCH_PLACEHOLDERS = [
  "Search spaces…",
  "Hotels in Kenya",
  "Apartments in Ghana…",
  "Boutique hotels",
  "Search by country…",
];

export type StaySubcategory = { id: string | number; name: string };

type StaySearchBarProps = {
  subcategories: StaySubcategory[];
};

const DATE_PICKER_CLASS =
  "h-5 w-full border-0 bg-transparent p-0 text-[14px] shadow-none [&>svg]:mr-2 [&>svg]:size-3.5 [&>svg]:text-[#017bef]";

export default function StaySearchBar({ subcategories }: StaySearchBarProps) {
  const history = useHistory();
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");
  const [destination, setDestination] = useState("");
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");

  // Rotate the destination placeholder while the input is empty
  useEffect(() => {
    if (destination) return;
    let intervalId: any;
    const timeoutId = setTimeout(() => {
      setPlaceholderIndex(prev => (prev + 1) % SEARCH_PLACEHOLDERS.length);
      intervalId = setInterval(() => {
        setPlaceholderIndex(prev => (prev + 1) % SEARCH_PLACEHOLDERS.length);
      }, 3500);
    }, 3500);
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [destination]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    let url = `/workspaces-search?type=stay&categoryId=2`;
    if (selectedSubcategoryId) url += `&subCategoryId=${selectedSubcategoryId}`;
    if (destination) {
      url += `&addressContains=${encodeURIComponent(destination)}&query=${encodeURIComponent(destination)}`;
    }
    if (checkIn) url += `&startDate=${encodeURIComponent(checkIn)}`;
    if (checkOut) url += `&endDate=${encodeURIComponent(checkOut)}`;
    history.push(url);
  };

  const pickDate = (val: any) => val?.startDate || (val?.specificDate && val.specificDate[0]) || "";

  const chip = (id: string, label: string) => (
    <button
      key={id || "all"}
      type="button"
      onClick={() => setSelectedSubcategoryId(id)}
      className={`whitespace-nowrap rounded-full px-[16px] py-1.5 text-[12px] font-semibold leading-4 transition-colors ${
        selectedSubcategoryId === id ? "bg-[#017bef] text-white" : "text-[#717182] hover:text-[#0a0a0a]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <form
      onSubmit={handleSearch}
      className="overflow-hidden rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white shadow-[0_8px_40px_0_rgba(0,0,0,0.08)]"
    >
      <div className="flex items-center gap-2 overflow-x-auto border-b-[1px] border-solid border-[#f5f8ff] px-[20px] pb-[12px] pt-[16px]">
        {chip("", "All")}
        {subcategories.map(sub => chip(String(sub.id), sub.name))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
        <div className="border-b-[1px] border-solid border-[#e5e7eb] px-[20px] py-[16px] sm:col-span-2 lg:border-b-0 lg:border-r-[1px]">
          <label className="block text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#717182]">
            Destination
          </label>
          <div className="flex items-center gap-2 pt-1">
            <MapPin className="size-3.5 shrink-0 text-[#017bef]" />
            <div className="relative h-5 flex-1 overflow-hidden">
              <input
                type="text"
                value={destination}
                onChange={e => setDestination(e.target.value)}
                autoComplete="off"
                aria-label="Destination"
                className="relative z-10 size-full border-0 bg-transparent p-0 text-[14px] text-[#0a0a0a] outline-none focus:outline-none focus:ring-0"
              />
              {!destination && (
                <div className="pointer-events-none absolute inset-0 flex items-center text-[14px] text-[#717182]">
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={placeholderIndex}
                      initial={{ y: -16, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: 16, opacity: 0 }}
                      transition={{ duration: 0.5, ease: "easeInOut" }}
                      className="block w-full truncate whitespace-nowrap text-left"
                    >
                      {SEARCH_PLACEHOLDERS[placeholderIndex]}
                    </motion.span>
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-b-[1px] border-solid border-[#e5e7eb] px-[20px] py-[16px] lg:border-b-0 lg:border-r-[1px]">
          <label className="block text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#717182]">
            Check-in
          </label>
          <div className="pt-1">
            <SmartDatePicker
              value={(checkIn ? { startDate: checkIn, endDate: checkIn } : null) as any}
              onChange={(val: any) => setCheckIn(pickDate(val))}
              placeholder="Add date"
              className={DATE_PICKER_CLASS}
            />
          </div>
        </div>

        <div className="border-b-[1px] border-solid border-[#e5e7eb] px-[20px] py-[16px] lg:border-b-0 lg:border-r-[1px]">
          <label className="block text-[12px] font-semibold uppercase leading-4 tracking-[1.2px] text-[#717182]">
            Check-out
          </label>
          <div className="pt-1">
            <SmartDatePicker
              value={(checkOut ? { startDate: checkOut, endDate: checkOut } : null) as any}
              onChange={(val: any) => setCheckOut(pickDate(val))}
              placeholder="Add date"
              className={DATE_PICKER_CLASS}
            />
          </div>
        </div>

        <div className="flex items-center px-[16px] py-[12px] sm:col-span-2 lg:col-span-1">
          <button
            type="submit"
            className="flex w-full items-center justify-center gap-2 rounded-[14px] bg-[#017bef] px-6 py-[12px] text-[14px] font-semibold leading-5 text-white drop-shadow-[0_2px_6px_rgba(1,123,239,0.25)] transition-colors hover:bg-[#0062c0]"
          >
            <Search className="size-[15px]" />
            Search Stays
          </button>
        </div>
      </div>
    </form>
  );
}
